import type { Coordinate } from '../models/editor-feature';
import { isUsableScale, type Scale } from './measurement';
import { deskShapeRing, type DeskFootprint } from './desk-symbols';

/**
 * Stamping a whole arrangement of desks in one go.
 *
 * A bench of four, a row of three, a cluster of six: these are what offices are actually laid
 * out in, and placing one desk at a time means twelve clicks, twelve room choices and twelve
 * undo steps for something the admin thinks of as one object. It also means the spacing is
 * whatever the mouse did.
 *
 * Every figure here is in metres and goes through the plan's scale, so a bench is 1.6 m desks
 * 0.1 m apart on any plan rather than a shape that happens to look right on one.
 */

export type DeskGroupKind = 'single' | 'pair' | 'bench4' | 'bench6' | 'row3' | 'cluster4' | 'custom';

export const DESK_GROUP_KINDS: readonly DeskGroupKind[] = [
  'single', 'pair', 'row3', 'bench4', 'cluster4', 'bench6', 'custom',
];

export interface DeskGroupShape {
  readonly columns: number;
  readonly rows: number;
  /**
   * Whether the rows face each other across a shared edge.
   *
   * A bench is two rows back to back — the desks touch and the people face away from each
   * other. A plain grid is aisles between rows. The difference is what makes a bench a bench.
   */
  readonly backToBack: boolean;
}

export const DESK_GROUP_SHAPES: Readonly<Record<DeskGroupKind, DeskGroupShape>> = {
  single: { columns: 1, rows: 1, backToBack: false },
  pair: { columns: 1, rows: 2, backToBack: true },
  bench4: { columns: 2, rows: 2, backToBack: true },
  bench6: { columns: 3, rows: 2, backToBack: true },
  row3: { columns: 3, rows: 1, backToBack: false },
  cluster4: { columns: 2, rows: 2, backToBack: false },
  // Only a starting point. A custom group carries its own shape in the options, because the
  // whole point of it is that the presets do not cover every office.
  custom: { columns: 4, rows: 1, backToBack: false },
};

/** The most desks a custom group may stamp at once, in either direction. */
export const MAXIMUM_CUSTOM_DESKS = 12;

const clampToGrid = (value: number): number => Math.min(
  MAXIMUM_CUSTOM_DESKS,
  Math.max(1, Math.round(value)),
);

/**
 * The arrangement a set of options describes.
 *
 * A preset reads its shape from the table; a custom group carries its own. One function for
 * both, so nothing downstream has to know which kind it was given.
 */
export const shapeOf = (kind: DeskGroupKind, custom?: DeskGroupShape): DeskGroupShape => {
  if (kind !== 'custom') {
    return DESK_GROUP_SHAPES[kind];
  }

  const shape = custom ?? DESK_GROUP_SHAPES.custom;

  return {
    columns: clampToGrid(shape.columns),
    rows: clampToGrid(shape.rows),
    backToBack: shape.backToBack,
  };
};

/** How many seats a group creates, which is what the admin is really choosing. */
export const seatCountOf = (kind: DeskGroupKind, custom?: DeskGroupShape): number => {
  const shape = shapeOf(kind, custom);
  return shape.columns * shape.rows;
};

export interface DeskGroupOptions {
  readonly kind: DeskGroupKind;
  /** The arrangement, when the kind is `custom`. Ignored for every preset. */
  readonly custom?: DeskGroupShape;
  readonly footprint: DeskFootprint;
  /** Between desks side by side, in metres. Zero is a true bench, with the desks touching. */
  readonly gapMetres: number;
  /** Aisle between rows that are not back to back, in metres. */
  readonly aisleMetres: number;
  /**
   * Between the backs of two rows that are back to back, in metres.
   *
   * Zero is a true bench, with the rows sharing an edge. Anything above it is the run of floor
   * some offices leave down the middle of a bench for cables, a screen rail or a planter — the
   * desks still face away from each other, which is what makes it one bench rather than two.
   */
  readonly backGapMetres: number;
  /** Degrees clockwise. The whole arrangement turns together. */
  readonly rotationDegrees: number;
}

/**
 * The spacing a stamped group starts with, in metres.
 *
 * The gap was 0.1 m, which is two desks all but touching: correct for a true bench and wrong
 * for everything else, and tight enough that a desk's icon ran onto the desk beside it. Two
 * fifths of a metre is the elbow room a desk actually gets, and both figures are editable in
 * the picker before anything is placed.
 */
export const DEFAULT_GROUP_OPTIONS: Omit<DeskGroupOptions, 'kind' | 'footprint'> = {
  gapMetres: 0.4,
  aisleMetres: 1.2,
  // Zero, because a bench whose rows do not touch is not what anybody means by a bench. It is
  // offered rather than assumed, which is the difference from before: the rows used to be
  // welded together with no way to part them.
  backGapMetres: 0,
  rotationDegrees: 0,
};

/** Spacing is in metres and a floor is not a warehouse: anything past this is a typo. */
export const MAXIMUM_SPACING_METRES = 10;

export interface DeskSeat {
  /** Where the desk marker goes. */
  readonly at: Coordinate;
  /** The furniture outline, ready to become a desk shape. */
  readonly ring: Coordinate[];
  readonly rotationDegrees: number;
}

const rotatePoint = (point: Coordinate, degrees: number): Coordinate => {
  if (degrees === 0) {
    return point;
  }

  const radians = (-degrees * Math.PI) / 180;

  return [
    point[0] * Math.cos(radians) - point[1] * Math.sin(radians),
    point[0] * Math.sin(radians) + point[1] * Math.cos(radians),
  ];
};

/**
 * Where each desk of a group goes, around the point that was clicked.
 *
 * Centred on that point rather than starting from it: a bench of six stamped at the middle of
 * a room lands in the middle of the room, which is what the admin aimed at. Anchoring at a
 * corner means every stamp has to be dragged afterwards.
 *
 * Returns nothing on a plan with no scale. Every dimension here is in metres, so without a
 * scale there is no arrangement to lay out — and a group stamped at arbitrary units would
 * scatter desks across the building or pile them on one spot.
 */
export const deskGroupSeats = (
  centre: Coordinate,
  options: DeskGroupOptions,
  scale: Scale,
): DeskSeat[] => {
  if (!isUsableScale(scale)) {
    return [];
  }

  const shape = shapeOf(options.kind, options.custom);
  const width = options.footprint.widthMetres * scale;
  const depth = options.footprint.depthMetres * scale;
  const gap = options.gapMetres * scale;
  // One figure, named differently depending on what is on the other side of it: an aisle people
  // walk down, or the strip between two desks' backs. Keeping them apart means turning a row of
  // desks back to back does not inherit a 1.2 m aisle and stop being a bench.
  const rowGap = (shape.backToBack ? options.backGapMetres : options.aisleMetres) * scale;

  const rowPitch = depth + rowGap;
  const columnPitch = width + gap;

  const totalWidth = (shape.columns * columnPitch) - gap;
  const totalDepth = (shape.rows * rowPitch) - rowGap;

  const seats: DeskSeat[] = [];

  for (let row = 0; row < shape.rows; row += 1) {
    for (let column = 0; column < shape.columns; column += 1) {
      // Laid out around (0, 0) and then turned as one, so the whole arrangement rotates
      // rather than each desk spinning on its own centre.
      const offset: Coordinate = [
        (column * columnPitch) - (totalWidth / 2) + (width / 2),
        (row * rowPitch) - (totalDepth / 2) + (depth / 2),
      ];

      const turned = rotatePoint(offset, options.rotationDegrees);
      const at: Coordinate = [centre[0] + turned[0], centre[1] + turned[1]];

      // The far row of a bench faces the other way. Recorded on the desk as well as drawn,
      // because a desk's rotation is what the app draws its marker with.
      const facing = shape.backToBack && row >= shape.rows / 2
        ? options.rotationDegrees + 180
        : options.rotationDegrees;

      seats.push({
        at,
        ring: deskShapeRing(at, options.footprint, scale, facing),
        rotationDegrees: ((facing % 360) + 360) % 360,
      });
    }
  }

  return seats;
};
